/**
 * 🚗 Vehicle Dashboard Page
 * 
 * Dashboard para contagem de veículos com:
 * - Gráfico de fluxo por hora (24h)
 * - Cards com totais do dia por tipo
 * - Filtros: data, tipo de veículo
 * - Botão para ver streaming ao vivo
 * - Comparação com dia anterior
 */

import React, { useEffect, useState, useRef } from 'react';
import {
  Car,
  Bike,
  Bus,
  Truck,
  TrendingUp,
  TrendingDown,
  Calendar,
  Eye,
  Filter,
  Download,
  Users,
  Loader2,
} from 'lucide-react';
import { APIConfig } from '../data/config/APIConfig';
import {
  DayObservationService,
  type DayObservation,
} from '../data/services/DayObservationService';
import { useToast } from '../components/common/Toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { VehiclePeopleFlowPaywall } from '../components/VehiclePeopleFlowPaywall';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface HourlyFlow {
  hour: number;
  total_vehicles: number;
  cars: number;
  motorcycles: number;
  buses: number;
  trucks: number;
}

interface DailyTotal {
  date: string;
  total_vehicles: number;
  cars: number;
  motorcycles: number;
  buses: number;
  trucks: number;
  peak_hour: number | null;
  peak_hour_count: number | null;
}

interface MonthlyData {
  month: string;
  total_vehicles: number;
  cars: number;
  motorcycles: number;
  buses: number;
  trucks: number;
  daily_average: number;
  growth_percentage: number | null;
}

interface MonthlyComparison {
  camera_id: string;
  camera_name: string;
  months: MonthlyData[];
}

interface VehicleStats {
  camera_id: string;
  camera_name: string;
  organization_name?: string;
  timezone?: string;
  date: string;
  hourly_flow: HourlyFlow[];
  daily_total: DailyTotal;
}

interface Camera {
  id: string;
  name: string;
  location: string;
  detection_mode: string;
  public_rtsp_url: string | null;
}

// 👥 Demographics Interfaces
interface GenderDistribution {
  male: number;
  female: number;
  male_percentage: number;
  female_percentage: number;
  total: number;
}

interface AgeDistribution {
  age_0_2: number;
  age_3_9: number;
  age_10_20: number;
  age_25_32: number;
  age_38_43: number;
  age_48_53: number;
  age_60_100: number;
  total: number;
}

interface HourlyPeopleFlow {
  hour: number;
  people_count: number;
  vehicles_count: number;
  avg_people_per_vehicle: number;
}

interface WeeklyFlow {
  day_of_week: string;
  date: string;
  day_label?: string; // Ex: "Ter 24/12"
  vehicles_count: number;
  people_count: number;
}

export default function VehicleDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast, ToastContainer } = useToast();
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [selectedCameras, setSelectedCameras] = useState<string[]>([]);
  const [cameraDropdownOpen, setCameraDropdownOpen] = useState(false);
  const cameraDropdownRef = useRef<HTMLDivElement>(null);
  
  // 🌎 Timezone da organização (ref para evitar problemas de ordem de declaração)
  const orgTimezoneRef = useRef<string>('America/Manaus');

  // Função para obter data no timezone da organização
  const getLocalDate = () => {
    const now = new Date();
    const orgDate = new Date(now.toLocaleString('en-US', { 
      timeZone: orgTimezoneRef.current 
    }));
    
    const year = orgDate.getFullYear();
    const month = String(orgDate.getMonth() + 1).padStart(2, '0');
    const day = String(orgDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Período de datas (padrão: hoje no fuso local)
  const [startDate, setStartDate] = useState<string>(getLocalDate());
  const [endDate, setEndDate] = useState<string>(getLocalDate());
  
  // 🎯 Datas aplicadas (usadas para fazer a requisição)
  const [appliedStartDate, setAppliedStartDate] = useState<string>(getLocalDate());
  const [appliedEndDate, setAppliedEndDate] = useState<string>(getLocalDate());
  
  const [stats, setStats] = useState<VehicleStats | null>(null);
  const [monthlyComparison, setMonthlyComparison] = useState<MonthlyComparison | null>(null);
  const [initialLoading, setInitialLoading] = useState(true); // Loading apenas na primeira carga
  const [filterLoading, setFilterLoading] = useState(false); // Loading visual do filtro (UX)
  const [isRefreshing, setIsRefreshing] = useState(false); // Flag para evitar múltiplas requisições simultâneas
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [monthsCount, setMonthsCount] = useState(6);
  
  // 📅 Controle de filtro de data ativo (feedback visual)
  const [activeDateFilter, setActiveDateFilter] = useState<'today' | '7days' | '30days' | 'custom' | null>('today');

  // 👥 People Flow Feature
  const [orgData, setOrgData] = useState<any>(null);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [genderData, setGenderData] = useState<GenderDistribution | null>(null);
  const [ageData, setAgeData] = useState<AgeDistribution | null>(null);
  const [hourlyPeopleData, setHourlyPeopleData] = useState<HourlyPeopleFlow[]>([]);
  const [weeklyFlowData, setWeeklyFlowData] = useState<WeeklyFlow[]>([]);
  const [weeklyObservations, setWeeklyObservations] = useState<DayObservation[]>([]);
  const [loadingDemographics, setLoadingDemographics] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  // 📅 Observações de dias (últimos 7 dias) — enriquece o gráfico semanal
  // de veículos, igual ao dashboard de pessoas/shopping.
  useEffect(() => {
    let cancelled = false;
    const loadWeeklyObservations = async () => {
      try {
        const today = new Date();
        const weekAgo = new Date();
        weekAgo.setDate(today.getDate() - 6);
        const fmt = (d: Date) =>
          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
            d.getDate(),
          ).padStart(2, '0')}`;
        const obs = await DayObservationService.list(fmt(weekAgo), fmt(today));
        if (!cancelled) setWeeklyObservations(obs || []);
      } catch {
        if (!cancelled) setWeeklyObservations([]);
      }
    };
    loadWeeklyObservations();
    return () => {
      cancelled = true;
    };
  }, []);

  const observationsByDate = React.useMemo(() => {
    const map: Record<string, DayObservation[]> = {};
    weeklyObservations.forEach((obs) => {
      const key = (obs.date || '').slice(0, 10);
      if (!map[key]) map[key] = [];
      map[key].push(obs);
    });
    return map;
  }, [weeklyObservations]);

  // 🔒 Verificar permissão para People Flow
  useEffect(() => {
    const loadOrganization = async () => {
      if (user?.is_superuser) {
        // Super admin tem acesso total
        setCheckingAccess(false);
        return;
      }

      if (!user?.organization_id) {
        setCheckingAccess(false);
        return;
      }

      try {
        const api = APIConfig.getInstance();
        const response = await api.get(`/api/v1/organizations/${user.organization_id}`);
        setOrgData(response.data);
      } catch (err) {
        console.error('Erro ao carregar organização:', err);
      } finally {
        setCheckingAccess(false);
      }
    };

    loadOrganization();
  }, [user]);

  // 🌎 Atualizar ref do timezone quando a organização ou a resposta de stats
  // carregar. O fallback (Manaus) é usado apenas antes de a organização ser
  // conhecida; superadmins não carregam orgData, então usamos o timezone que o
  // backend devolve em /vehicles/stats.
  useEffect(() => {
    const timezone = orgData?.operating_timezone || stats?.timezone;
    if (!timezone) return;
    if (orgTimezoneRef.current === timezone) return;
    orgTimezoneRef.current = timezone;

    if (activeDateFilter === 'today') {
      const today = getLocalDate();
      setStartDate(today);
      setEndDate(today);
      setAppliedStartDate(today);
      setAppliedEndDate(today);
    }
  }, [orgData, stats?.timezone, activeDateFilter]);

  // Fechar dropdown de câmeras ao clicar fora
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (cameraDropdownRef.current && !cameraDropdownRef.current.contains(e.target as Node)) {
        setCameraDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    loadCameras();
  }, []);

  // 🔧 FIX RACE CONDITION: selecionar câmera apenas após org estar carregada.
  // Antes: loadCameras chamava setSelectedCameras diretamente → useEffect disparava
  // com orgData=null → demographics pulados na primeira carga.
  // Agora: checkingAccess=false garante que orgData já está disponível.
  useEffect(() => {
    if (cameras.length > 0 && !checkingAccess && selectedCameras.length === 0) {
      setSelectedCameras([cameras[0].id]);
    }
  }, [cameras, checkingAccess]);

  useEffect(() => {
    if (selectedCameras.length > 0 && !filterLoading) {
      setFilterLoading(true);
      loadAllData();
    }
  // orgData removido das deps: a seleção de câmera agora já garante orgData pronto
  }, [selectedCameras, appliedStartDate, appliedEndDate, monthsCount]);

  // 🔄 Atualização automática a cada 30s (sem refresh visual da página)
  // Igual ao Dashboard de Shopping - usa React state, não recarrega página
  useEffect(() => {
    if (selectedCameras.length === 0) return;

    const interval = setInterval(async () => {
      // ⏸️ PAUSAR auto-refresh se filtragem estiver em andamento
      if (filterLoading) {
        console.warn('⏸️ Auto-refresh pausado: filtragem em andamento');
        return;
      }

      // ⚠️ Evitar múltiplas requisições simultâneas
      if (isRefreshing) {
        console.warn('⏭️ Auto-refresh pulado: requisição anterior ainda em andamento');
        return;
      }

      setIsRefreshing(true);
      try {
        // Recarrega dados SEM mostrar loading (auto-refresh silencioso)
        await Promise.allSettled([
          loadStats(),
          loadMonthlyComparison(),
          (orgData?.vehicle_people_flow_enabled || user?.is_superuser) ? loadDemographics() : Promise.resolve(),
        ]);
      } catch (err) {
        console.error('Erro no auto-refresh:', err);
        // ❌ NÃO mostra erro visual - apenas log
      } finally {
        setIsRefreshing(false);
      }
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, [selectedCameras, appliedStartDate, appliedEndDate, monthsCount, orgData, isRefreshing, filterLoading]);

  const loadCameras = async () => {
    try {
      setInitialLoading(true);
      const api = APIConfig.getInstance();
      const response = await api.get('/api/v1/cameras');
      
      // Filtrar apenas câmeras com detection_mode='vehicle'
      const vehicleCameras = response.data.filter(
        (cam: Camera) => cam.detection_mode === 'vehicle'
      );
      
      setCameras(vehicleCameras);
      // ⚠️ NÃO setar selectedCameras aqui — o useEffect [cameras, checkingAccess]
      // aguarda a org estar pronta antes de selecionar, evitando race condition
      // onde loadAllData rodava sem orgData → demographics pulados.

      // Sempre finaliza o loading, mesmo sem câmeras
      setInitialLoading(false);
    } catch (err: any) {
      console.error('Erro ao carregar câmeras:', err);
      setError('Erro ao carregar câmeras de veículos');
      setInitialLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      // Não bloqueia UI no auto-refresh, apenas valida
      const api = APIConfig.getInstance();
      
      // Validar se as datas estão preenchidas
      if (!appliedStartDate || appliedStartDate.trim() === '' || !appliedEndDate || appliedEndDate.trim() === '') {
        setStats(null);
        return;
      }
      
      // 🚀 Cache localStorage: 5 min para hoje, 30 min para datas históricas
      const statsCacheKey = `vstats_${orgTimezoneRef.current}_${selectedCameras.join('_')}_${appliedStartDate}_${appliedEndDate}`;
      const statsIsToday = appliedStartDate === appliedEndDate && appliedStartDate === getLocalDate();
      const statsCacheTTL = statsIsToday ? 5 : 30; // minutos
      const statsCached = localStorage.getItem(statsCacheKey);
      if (statsCached) {
        try {
          const { data: cachedStats, timestamp: cacheTs } = JSON.parse(statsCached);
          const ageMin = (Date.now() - new Date(cacheTs).getTime()) / 60000;
          if (ageMin < statsCacheTTL) {
            setStats(cachedStats);
            setError(null);
            return;
          }
        } catch { /* cache inválido, seguir para API */ }
      }

      const response = await api.get('/api/v1/vehicles/stats', {
        params: {
          ...(selectedCameras.length === 1
            ? { camera_id: selectedCameras[0] }
            : { camera_ids: selectedCameras.join(',') }),
          start_date: appliedStartDate,
          end_date: appliedEndDate,
        },
        timeout: 30000, // 30 segundos timeout (veículos tem muito dado)
      });
      
      setStats(response.data);
      localStorage.setItem(statsCacheKey, JSON.stringify({ data: response.data, timestamp: new Date().toISOString() }));
      setError(null); // ✅ Limpa erro apenas quando sucesso
    } catch (err: any) {
      console.error('❌ Erro ao carregar estatísticas:', err);
      // ⚠️ NÃO seta erro global - mantém dados antigos na tela
      // Apenas loga erro no console para debug
    }
  };

  const loadMonthlyComparison = async () => {
    try {
      // Não carregar comparação se não tiver câmera ou datas selecionadas
      if (selectedCameras.length === 0 || !appliedStartDate || appliedStartDate.trim() === '' || !appliedEndDate || appliedEndDate.trim() === '') {
        return;
      }
      
      const api = APIConfig.getInstance();
      
      // 🚀 Cache localStorage: 30 min (comparação mensal raramente muda)
      const monthlyCacheKey = `vmonthly_${orgTimezoneRef.current}_${selectedCameras.join('_')}_${monthsCount}`;
      const monthlyCached = localStorage.getItem(monthlyCacheKey);
      if (monthlyCached) {
        try {
          const { data: cachedMonthly, timestamp: cacheTs } = JSON.parse(monthlyCached);
          const ageMin = (Date.now() - new Date(cacheTs).getTime()) / 60000;
          if (ageMin < 30) {
            setMonthlyComparison(cachedMonthly);
            return;
          }
        } catch { /* cache inválido, seguir para API */ }
      }

      const response = await api.get('/api/v1/vehicles/monthly-comparison', {
        params: {
          ...(selectedCameras.length === 1
            ? { camera_id: selectedCameras[0] }
            : { camera_ids: selectedCameras.join(',') }),
          months: monthsCount,
        },
        timeout: 30000, // 30 segundos timeout
      });
      
      setMonthlyComparison(response.data);
      localStorage.setItem(monthlyCacheKey, JSON.stringify({ data: response.data, timestamp: new Date().toISOString() }));
    } catch (err: any) {
      console.error('Erro ao carregar comparação mensal:', err);
      // Não mostra erro, comparação é opcional
    }
  };

  // 🎯 Função centralizada que carrega TODOS os dados e controla loading
  const loadAllData = async () => {
    try {
      // Executar todas as chamadas em paralelo (não para se uma falhar)
      await Promise.allSettled([
        loadStats(),
        loadMonthlyComparison(),
        // 👥 Carregar demographics se feature habilitada
        (orgData?.vehicle_people_flow_enabled || user?.is_superuser) ? loadDemographics() : Promise.resolve(),
      ]);
    } finally {
      // ✅ GARANTIA: SEMPRE desativa loading (sucesso ou erro)
      setFilterLoading(false);
    }
  };

  // 👥 Carregar dados demográficos - COM CACHE
  const loadDemographics = async () => {
    if (selectedCameras.length === 0) return;
    
    setLoadingDemographics(true);
    try {
      const cacheKey = `demographics_${orgTimezoneRef.current}_${selectedCameras.join('_')}_${appliedStartDate}_${appliedEndDate}`;
      
      // 🚀 Tentar carregar do cache primeiro
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        try {
          const cachedData = JSON.parse(cached);
          const cacheTime = new Date(cachedData.timestamp);
          const now = new Date();
          const minutesDiff = (now.getTime() - cacheTime.getTime()) / (1000 * 60);

          // 🕐 Cache curto quando o período inclui HOJE (dados mudam a cada
          // minuto); 30 min para períodos históricos. Evita dashboard mostrando
          // contagem defasada (ex.: travada às 6h) após correção/backfill.
          const todayStr = getLocalDate();
          const cacheTtlMin = appliedEndDate === todayStr ? 5 : 30;

          if (minutesDiff < cacheTtlMin) {
            setGenderData(cachedData.gender);
            setAgeData(cachedData.age);
            setHourlyPeopleData(cachedData.hourly);
            
            // Processar dados semanais do cache para adicionar day_label
            const weeklyWithLabels = cachedData.weekly.map((item: WeeklyFlow) => {
              const [, month, day] = item.date.split('-');
              const dayShort = item.day_of_week.substring(0, 3);
              return {
                ...item,
                day_label: `${dayShort} ${day}/${month}`,
              };
            });
            setWeeklyFlowData(weeklyWithLabels);
            
            setLoadingDemographics(false);
            return;
          }
        } catch (e) {
          console.warn('Cache demográfico inválido, recarregando...');
        }
      }

      const api = APIConfig.getInstance();
      const cameraParams = selectedCameras.length === 1
        ? { camera_id: selectedCameras[0] }
        : { camera_ids: selectedCameras.join(',') };

      // ⚡ Executar todas as requisições em paralelo
      const [genderResponse, ageResponse, hourlyResponse, weeklyResponse] = await Promise.all([
        // Carregar distribuição de gênero
        api.get('/api/v1/vehicles/demographics/gender', {
          params: {
            ...cameraParams,
            start_date: appliedStartDate,
            end_date: appliedEndDate,
          },
          timeout: 30000, // 30 segundos timeout
        }),
        // Carregar distribuição de idade
        api.get('/api/v1/vehicles/demographics/age', {
          params: {
            ...cameraParams,
            start_date: appliedStartDate,
            end_date: appliedEndDate,
          },
          timeout: 30000, // 30 segundos timeout
        }),
        // Carregar fluxo de pessoas por hora
        api.get('/api/v1/vehicles/demographics/hourly', {
          params: {
            ...cameraParams,
            start_date: appliedStartDate,
            end_date: appliedEndDate,
          },
          timeout: 30000, // 30 segundos timeout
        }),
        // Carregar fluxo semanal (últimos 7 dias)
        api.get('/api/v1/vehicles/demographics/weekly', {
          params: {
            ...cameraParams,
          },
          timeout: 30000, // 30 segundos timeout
        }),
      ]);

      setGenderData(genderResponse.data);
      setAgeData(ageResponse.data);
      setHourlyPeopleData(hourlyResponse.data);
      
      // Processar dados semanais para adicionar day_label
      const weeklyWithLabels = weeklyResponse.data.map((item: WeeklyFlow) => {
        const [, month, day] = item.date.split('-');
        const dayShort = item.day_of_week.substring(0, 3); // "Terça" -> "Ter"
        return {
          ...item,
          day_label: `${dayShort} ${day}/${month}`,
        };
      });
      setWeeklyFlowData(weeklyWithLabels);

      // 💾 Salvar no cache
      localStorage.setItem(cacheKey, JSON.stringify({
        gender: genderResponse.data,
        age: ageResponse.data,
        hourly: hourlyResponse.data,
        weekly: weeklyResponse.data,
        timestamp: new Date().toISOString(),
      }));

    } catch (err: any) {
      console.error('Erro ao carregar demographics:', err);
      // Se feature não estiver habilitada, API retorna 403
      if (err.response?.status === 403) {
        setGenderData(null);
        setAgeData(null);
        setHourlyPeopleData([]);
        setWeeklyFlowData([]);
      }
    } finally {
      setLoadingDemographics(false);
    }
  };

  const handleViewStream = () => {
    if (selectedCameras.length > 0) {
      navigate(`/vehicles/stream/${selectedCameras[0]}`);
    }
  };

  const handleExportPDF = async () => {
    setExportingPdf(true);
    try {
      const api = APIConfig.getInstance();
      const cameraParams = selectedCameras.length > 1
        ? { camera_ids: selectedCameras.join(',') }
        : { camera_id: selectedCameras[0] };
      const response = await api.get('/api/v1/vehicles/export-pdf', {
        params: {
          ...cameraParams,
          start_date: startDate,
          end_date: endDate,
        },
        responseType: 'blob',
        timeout: 90000, // 90 segundos - geração de PDF pode demorar
      });
      
      // Download do arquivo
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `veiculos_${startDate}_a_${endDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showToast('PDF exportado com sucesso!', 'success');
    } catch (err: any) {
      console.error('Erro ao exportar PDF:', err);
      showToast(`Erro ao exportar PDF: ${err.response?.data?.detail || err.message}`, 'error');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const api = APIConfig.getInstance();
      const cameraParams = selectedCameras.length > 1
        ? { camera_ids: selectedCameras.join(',') }
        : { camera_id: selectedCameras[0] };
      const response = await api.get('/api/v1/vehicles/export-excel', {
        params: {
          ...cameraParams,
          start_date: startDate,
          end_date: endDate,
        },
        responseType: 'blob',
        timeout: 90000, // 90 segundos - geração de Excel pode demorar
      });
      
      // Download do arquivo
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `veiculos_${startDate}_a_${endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showToast('Excel exportado com sucesso!', 'success');
    } catch (err: any) {
      console.error('Erro ao exportar Excel:', err);
      showToast(`Erro ao exportar Excel: ${err.response?.data?.detail || err.message}`, 'error');
    } finally {
      setExportingExcel(false);
    }
  };

  // Helper functions para quick date ranges (usam o timezone da organização)
  const setLast7Days = () => {
    const now = new Date();
    const orgNow = new Date(now.toLocaleString('en-US', { timeZone: orgTimezoneRef.current }));
    
    const end = new Date(orgNow);
    const start = new Date(orgNow);
    start.setDate(start.getDate() - 6);
    
    const formatLocalDate = (date: Date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };
    
    const formattedStart = formatLocalDate(start);
    const formattedEnd = formatLocalDate(end);
    
    setStartDate(formattedStart);
    setEndDate(formattedEnd);
    setActiveDateFilter('7days'); // ✨ Atualizar estado ativo
    // Aplicar filtro imediatamente
    setAppliedStartDate(formattedStart);
    setAppliedEndDate(formattedEnd);
  };

  const setLast30Days = () => {
    const now = new Date();
    const orgNow = new Date(now.toLocaleString('en-US', { timeZone: orgTimezoneRef.current }));
    
    const end = new Date(orgNow);
    const start = new Date(orgNow);
    start.setDate(start.getDate() - 29);
    
    const formatLocalDate = (date: Date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };
    
    const formattedStart = formatLocalDate(start);
    const formattedEnd = formatLocalDate(end);
    
    setStartDate(formattedStart);
    setEndDate(formattedEnd);
    setActiveDateFilter('30days'); // ✨ Atualizar estado ativo
    // Aplicar filtro imediatamente
    setAppliedStartDate(formattedStart);
    setAppliedEndDate(formattedEnd);
  };

  const setToday = () => {
    const now = new Date();
    const orgNow = new Date(now.toLocaleString('en-US', { timeZone: orgTimezoneRef.current }));
    const today = `${orgNow.getFullYear()}-${String(orgNow.getMonth() + 1).padStart(2, '0')}-${String(orgNow.getDate()).padStart(2, '0')}`;
    setStartDate(today);
    setEndDate(today);
    setActiveDateFilter('today'); // ✨ Atualizar estado ativo
    // Aplicar filtro imediatamente
    setAppliedStartDate(today);
    setAppliedEndDate(today);
  };

  // 🎯 Função para aplicar filtro manualmente (botão "Filtrar")
  const handleApplyFilter = () => {
    // Validar datas antes de aplicar
    if (!startDate || startDate.trim() === '' || !endDate || endDate.trim() === '') {
      setError('Por favor, selecione as datas de início e fim');
      return;
    }
    
    if (new Date(endDate) < new Date(startDate)) {
      setError('Data final deve ser maior ou igual à data inicial');
      return;
    }
    
    // ✅ NÃO setar filterLoading manualmente - deixar useEffect gerenciar
    // Aplicar filtro (useEffect detecta mudança e carrega dados automaticamente)
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setError(null);
    setActiveDateFilter('custom'); // Marcar como filtro customizado
  };

  const getFilteredChartData = () => {
    if (!stats) return [];
    
    return stats.hourly_flow.map((h) => ({
      hour: `${h.hour.toString().padStart(2, '0')}:00`,
      total: h.total_vehicles,
      carros: h.cars,
      motos: h.motorcycles,
      ônibus: h.buses,
      'caminhões/picapes': h.trucks,
    }));
  };

  const formatMonth = (monthStr: string) => {
    const parts = monthStr.split('-');
    const monthNames = [
      'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
      'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
    ];
    return `${monthNames[parseInt(parts[1]) - 1]}/${parts[0]}`;
  };

  const getMonthlyChartData = () => {
    if (!monthlyComparison) return [];
    
    return monthlyComparison.months.map((m) => ({
      month: formatMonth(m.month),
      Total: m.total_vehicles,
      Carros: m.cars,
      Motos: m.motorcycles,
      Ônibus: m.buses,
      'Caminhões/Picapes': m.trucks,
    }));
  };

  const getGrowthColor = (growth: number) => {
    if (growth > 0) return 'text-green-600 dark:text-green-400';
    if (growth < 0) return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  const getGrowthIcon = (growth: number) => {
    if (growth > 0) return <TrendingUp className="w-4 h-4" />;
    if (growth < 0) return <TrendingDown className="w-4 h-4" />;
    return null;
  };

  // Apenas mostra loading na primeira carga (não no auto-refresh)
  if (initialLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">
            {checkingAccess ? 'Verificando permissões...' : 'Carregando dashboard...'}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 max-w-md">
          <p className="text-red-800 dark:text-red-200">{error}</p>
          <button
            onClick={() => {
              setError(null);
              loadCameras();
            }}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <main className="px-4 sm:px-6 lg:px-8 pb-12 pt-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                🚗 Contagem de Veículos
                {stats?.organization_name && (
                  <span className="text-lg sm:text-xl font-normal text-gray-600 dark:text-gray-400 ml-2">
                    - {stats.organization_name}
                  </span>
                )}
              </h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
                Análise de fluxo de tráfego em tempo real
              </p>
              
              {/* Indicador de Filtragem */}
              {filterLoading && (
                <div className="flex items-center gap-2 text-xs bg-yellow-100 dark:bg-yellow-900/30 px-3 py-1.5 rounded-lg mt-2 w-fit">
                  <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
                  <span className="text-yellow-700 dark:text-yellow-400 font-medium">
                    Carregando dados do filtro...
                  </span>
                </div>
              )}
            </div>
            
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <button
                onClick={handleViewStream}
                disabled={selectedCameras.length === 0}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition text-sm sm:text-base ${
                  selectedCameras.length > 0
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                }`}
              >
                <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Ver Ao Vivo</span>
                <span className="sm:hidden">Ao Vivo</span>
              </button>
              
              <button
                onClick={handleExportPDF}
                disabled={selectedCameras.length === 0 || !stats || exportingPdf || exportingExcel}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition text-sm sm:text-base ${
                  selectedCameras.length > 0 && stats && !exportingPdf && !exportingExcel
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                }`}
              >
                {exportingPdf ? (
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                )}
                <span className="hidden sm:inline">{exportingPdf ? 'Gerando PDF...' : 'Exportar PDF'}</span>
                <span className="sm:hidden">{exportingPdf ? '...' : 'PDF'}</span>
              </button>
              
              <button
                onClick={handleExportExcel}
                disabled={selectedCameras.length === 0 || !stats || exportingPdf || exportingExcel}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition text-sm sm:text-base ${
                  selectedCameras.length > 0 && stats && !exportingPdf && !exportingExcel
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                }`}
              >
                {exportingExcel ? (
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                )}
                <span className="hidden sm:inline">{exportingExcel ? 'Gerando Excel...' : 'Exportar Excel'}</span>
                <span className="sm:hidden">{exportingExcel ? '...' : 'Excel'}</span>
              </button>
            </div>
          </div>

          {/* Filtros */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {/* Seleção de Câmera — multi-select */}
              <div ref={cameraDropdownRef} className="relative">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Câmera
                </label>
              {cameras.length > 0 ? (
                <>
                  <button
                    type="button"
                    onClick={() => setCameraDropdownOpen(prev => !prev)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm text-left flex items-center justify-between"
                  >
                    <span className="truncate">
                      {selectedCameras.length === 0
                        ? 'Selecione uma câmera'
                        : selectedCameras.length === 1
                          ? (cameras.find(c => c.id === selectedCameras[0])?.name ?? selectedCameras[0])
                          : `${selectedCameras.length} câmeras selecionadas`}
                    </span>
                    <svg
                      className={`w-4 h-4 ml-2 flex-shrink-0 transition-transform ${cameraDropdownOpen ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {cameraDropdownOpen && (
                    <div className="absolute z-30 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                      {cameras.map((cam) => (
                        <label key={cam.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm">
                          <input
                            type="checkbox"
                            checked={selectedCameras.includes(cam.id)}
                            onChange={() => {
                              setSelectedCameras(prev =>
                                prev.includes(cam.id)
                                  ? prev.filter(id => id !== cam.id)
                                  : [...prev, cam.id]
                              );
                            }}
                            className="accent-blue-500"
                          />
                          <span className="text-gray-700 dark:text-gray-200 truncate">{cam.name} - {cam.location}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-sm">
                  Nenhuma câmera de veículos cadastrada
                </div>
              )}
              </div>

            {/* Período de Datas - Data Início */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Data Início
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value || getLocalDate());
                  setActiveDateFilter('custom'); // ✨ Marcar como custom ao editar manualmente
                }}
                max={getLocalDate()}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Período de Datas - Data Fim */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Data Fim
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value || getLocalDate());
                  setActiveDateFilter('custom'); // ✨ Marcar como custom ao editar manualmente
                }}
                max={getLocalDate()}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Filtro por Tipo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tipo de Veículo
              </label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="all">Todos</option>
                <option value="car">Carros</option>
                <option value="motorcycle">Motos</option>
                <option value="bus">Ônibus</option>
                <option value="truck">Caminhões/Picapes</option>
              </select>
            </div>
            
            {/* Botão Filtrar */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                &nbsp;
              </label>
              <button
                onClick={handleApplyFilter}
                disabled={selectedCameras.length === 0 || filterLoading}
                className={`w-full px-4 py-2 rounded-lg transition flex items-center justify-center gap-2 ${
                  selectedCameras.length > 0 && !filterLoading
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                }`}
              >
                {filterLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span>Carregando...</span>
                  </>
                ) : (
                  <>
                    <Filter className="w-4 h-4" />
                    Filtrar
                  </>
                )}
              </button>
            </div>
          </div>
          
          {/* Botões Rápidos */}
          <div className="flex flex-wrap gap-2 mt-4">
            <button
              onClick={setToday}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition ${
                activeDateFilter === 'today'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Hoje
            </button>
            <button
              onClick={setLast7Days}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition ${
                activeDateFilter === '7days'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Últimos 7 dias
            </button>
            <button
              onClick={setLast30Days}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition ${
                activeDateFilter === '30days'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Últimos 30 dias
            </button>
          </div>
        </div>
      </div>

        {/* Badge de Loading Visual (UX) */}
        {filterLoading && (
          <div className="mb-4 animate-pulse">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-center gap-3">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
              <div>
                <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                  Carregando dados de {startDate} até {endDate}...
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Aguarde enquanto buscamos as estatísticas
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Cards de Totais */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-6 xl:gap-8 mb-6">
          {/* Total Geral */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 sm:p-4 lg:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Car className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
            {stats?.daily_total.total_vehicles || 0}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Total de Veículos</p>
          {stats && stats.daily_total.peak_hour !== null && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Pico: {stats.daily_total.peak_hour}h (
              {stats.daily_total.peak_hour_count} veículos)
            </p>
          )}
        </div>

          {/* Carros */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 sm:p-4 lg:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Car className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
            {stats?.daily_total.cars || 0}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Carros</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {stats && stats.daily_total.total_vehicles > 0
              ? (
                  (stats.daily_total.cars / stats.daily_total.total_vehicles) *
                  100
                ).toFixed(1)
              : 0}
            % do total
          </p>
        </div>

          {/* Motos */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 sm:p-4 lg:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
              <Bike className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
            {stats?.daily_total.motorcycles || 0}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Motos</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {stats && stats.daily_total.total_vehicles > 0
              ? (
                  (stats.daily_total.motorcycles /
                    stats.daily_total.total_vehicles) *
                  100
                ).toFixed(1)
              : 0}
            % do total
          </p>
        </div>

          {/* Ônibus */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 sm:p-4 lg:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Bus className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
            {stats?.daily_total.buses || 0}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Ônibus</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {stats && stats.daily_total.total_vehicles > 0
              ? (
                  (stats.daily_total.buses / stats.daily_total.total_vehicles) *
                  100
                ).toFixed(1)
              : 0}
            % do total
          </p>
        </div>

          {/* Caminhões/Picapes */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 sm:p-4 lg:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <Truck className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
            {stats?.daily_total.trucks || 0}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Caminhões/Picapes</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {stats && stats.daily_total.total_vehicles > 0
              ? (
                  (stats.daily_total.trucks / stats.daily_total.total_vehicles) *
                  100
                ).toFixed(1)
              : 0}
            % do total
          </p>
        </div>
      </div>

        {/* Mensagem quando não há câmeras */}
        {cameras.length === 0 && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 sm:p-6 mb-6">
          <div className="flex items-center gap-3">
            <Car className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
            <div>
              <h3 className="font-semibold text-yellow-900 dark:text-yellow-200">
                Nenhuma câmera de veículos cadastrada
              </h3>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                Cadastre uma câmera com modo de detecção "vehicle" para começar a monitorar o fluxo de veículos.
              </p>
            </div>
            </div>
          </div>
        )}

        {/* Mensagem quando não há dados */}
        {!stats && cameras.length > 0 && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 sm:p-6 mb-6">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <div>
              <h3 className="font-semibold text-blue-900 dark:text-blue-200">
                Aguardando detecções
              </h3>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  Nenhum dado disponível para a data selecionada. As detecções aparecerão aqui assim que começarem.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Gráfico de Fluxo por Hora */}
        {stats && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 lg:p-8 xl:p-10 mb-6">
            <h2 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Fluxo de Veículos por Hora
            </h2>
            
            <ResponsiveContainer width="100%" height={400}>
            <LineChart data={getFilteredChartData()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip />
              <Legend />
              {(filterType === 'all' || filterType === 'car') && (
                <Line
                  type="monotone"
                  dataKey="carros"
                  stroke="#10b981"
                  strokeWidth={2}
                  name="Carros"
                />
              )}
              {(filterType === 'all' || filterType === 'motorcycle') && (
                <Line
                  type="monotone"
                  dataKey="motos"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  name="Motos"
                />
              )}
              {(filterType === 'all' || filterType === 'bus') && (
                <Line
                  type="monotone"
                  dataKey="ônibus"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  name="Ônibus"
                />
              )}
              {(filterType === 'all' || filterType === 'truck') && (
                <Line
                  type="monotone"
                  dataKey="caminhões/picapes"
                  stroke="#ef4444"
                  strokeWidth={2}
                  name="Caminhões/Picapes"
                />
              )}
              {filterType === 'all' && (
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  name="Total"
                />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Comparação Mensal */}
        {monthlyComparison && (
          <>
            {/* Filtro de Meses */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                    Comparação Mensal
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Evolução mês a mês com crescimento percentual
                  </p>
                </div>
                <div className="flex items-center gap-2 sm:gap-4">
                  <label className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                    Período:
                  </label>
                  <select
                    value={monthsCount}
                    onChange={(e) => setMonthsCount(parseInt(e.target.value))}
                    className="px-2 sm:px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value={3}>3 meses</option>
                    <option value={6}>6 meses</option>
                    <option value={12}>12 meses</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Gráfico de Totais */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 lg:p-8 xl:p-10 mb-6">
              <h3 className="text-lg lg:text-xl font-bold text-gray-900 dark:text-white mb-4">
                Evolução Mensal - Total de Veículos
              </h3>
              
              <ResponsiveContainer width="100%" height={350}>
              <BarChart data={getMonthlyChartData()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="month" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1F2937', 
                    border: '1px solid #374151',
                    borderRadius: '0.5rem'
                  }}
                  labelStyle={{ color: '#F3F4F6' }}
                />
                <Legend />
                  <Bar dataKey="Total" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Gráfico por Tipo */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 lg:p-8 xl:p-10 mb-6">
              <h3 className="text-lg lg:text-xl font-bold text-gray-900 dark:text-white mb-4">
                Evolução por Tipo de Veículo
              </h3>
              
              <ResponsiveContainer width="100%" height={350}>
              <LineChart data={getMonthlyChartData()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="month" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1F2937', 
                    border: '1px solid #374151',
                    borderRadius: '0.5rem'
                  }}
                  labelStyle={{ color: '#F3F4F6' }}
                />
                <Legend />
                <Line type="monotone" dataKey="Carros" stroke="#10b981" strokeWidth={2} />
                <Line type="monotone" dataKey="Motos" stroke="#f59e0b" strokeWidth={2} />
                <Line type="monotone" dataKey="Ônibus" stroke="#8b5cf6" strokeWidth={2} />
                  <Line type="monotone" dataKey="Caminhões/Picapes" stroke="#ef4444" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Tabela Comparativa */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                  Tabela Comparativa Detalhada
                </h3>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Mês
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Total
                      </th>
                      <th className="hidden sm:table-cell px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Carros
                      </th>
                      <th className="hidden md:table-cell px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Motos
                      </th>
                      <th className="hidden lg:table-cell px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Ônibus
                      </th>
                      <th className="hidden lg:table-cell px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Caminhões/Picapes
                      </th>
                      <th className="hidden xl:table-cell px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Média/Dia
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Cresc.
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {monthlyComparison.months.map((month, index) => (
                      <tr key={month.month} className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900'}>
                        <td className="px-3 sm:px-6 py-3 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100">
                          {formatMonth(month.month)}
                        </td>
                        <td className="px-3 sm:px-6 py-3 whitespace-nowrap text-xs sm:text-sm text-right text-gray-900 dark:text-gray-100 font-semibold">
                          {month.total_vehicles.toLocaleString('pt-BR')}
                        </td>
                        <td className="hidden sm:table-cell px-3 sm:px-6 py-3 whitespace-nowrap text-xs sm:text-sm text-right text-gray-700 dark:text-gray-200">
                          {month.cars.toLocaleString('pt-BR')}
                        </td>
                        <td className="hidden md:table-cell px-3 sm:px-6 py-3 whitespace-nowrap text-xs sm:text-sm text-right text-gray-700 dark:text-gray-200">
                          {month.motorcycles.toLocaleString('pt-BR')}
                        </td>
                        <td className="hidden lg:table-cell px-3 sm:px-6 py-3 whitespace-nowrap text-xs sm:text-sm text-right text-gray-700 dark:text-gray-200">
                          {month.buses.toLocaleString('pt-BR')}
                        </td>
                        <td className="hidden lg:table-cell px-3 sm:px-6 py-3 whitespace-nowrap text-xs sm:text-sm text-right text-gray-700 dark:text-gray-200">
                          {month.trucks.toLocaleString('pt-BR')}
                        </td>
                        <td className="hidden xl:table-cell px-3 sm:px-6 py-3 whitespace-nowrap text-xs sm:text-sm text-right text-gray-700 dark:text-gray-200">
                          {month.daily_average.toFixed(1)}
                        </td>
                        <td className="px-3 sm:px-6 py-3 whitespace-nowrap text-xs sm:text-sm text-right">
                          {month.growth_percentage !== null ? (
                            <div className={`flex items-center justify-end gap-1 ${getGrowthColor(month.growth_percentage)}`}>
                              {getGrowthIcon(month.growth_percentage)}
                              <span className="font-semibold">
                                {month.growth_percentage > 0 ? '+' : ''}
                                {month.growth_percentage.toFixed(1)}%
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* 📅 OBSERVAÇÕES DE DIAS — disponível para TODOS os clientes de veículos */}
        {weeklyObservations.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-amber-500" />
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Observações de Dias
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Eventos e observações cadastrados nos últimos 7 dias
            </p>
            <div className="flex flex-wrap gap-2">
              {weeklyObservations.map((obs) => (
                <span
                  key={obs.id}
                  className="px-3 py-1.5 rounded-full text-xs font-medium border"
                  style={{
                    backgroundColor: `${obs.color}22`,
                    color: obs.color,
                    borderColor: obs.color,
                  }}
                  title={obs.description || obs.title}
                >
                  {new Date(`${obs.date.slice(0, 10)}T00:00:00`).toLocaleDateString(
                    'pt-BR',
                    { day: '2-digit', month: '2-digit' },
                  )}{' '}
                  · {obs.title}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* 👥 SEÇÃO DE DEMOGRAPHICS - PEOPLE FLOW */}
        {!checkingAccess && (
          <>
            {/* Divisor visual */}
            <div className="my-8 border-t-2 border-gray-200 dark:border-gray-700"></div>

            {/* Header da seção Demographics */}
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-8 h-8 text-purple-600" />
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Análise de Fluxo de Pessoas
                </h2>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Dados demográficos de pessoas detectadas na área monitorada
              </p>
            </div>

            {/* Verificar se feature está habilitada */}
            {!orgData?.vehicle_people_flow_enabled && !user?.is_superuser ? (
              <VehiclePeopleFlowPaywall />
            ) : (
              <>
                {/* Loading state */}
                {loadingDemographics && (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Carregando dados demográficos...</p>
                  </div>
                )}

                {/* Conteúdo Demographics */}
                {!loadingDemographics && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    {/* 1. Gráfico de Gênero - PieChart */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                        Distribuição por Gênero
                      </h3>
                      
                      {genderData && genderData.total > 0 ? (
                        <>
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={[
                                  { name: 'Masculino', value: genderData.male, percentage: genderData.male_percentage },
                                  { name: 'Feminino', value: genderData.female, percentage: genderData.female_percentage },
                                ]}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={(entry: any) => `${entry.name}: ${entry.percentage.toFixed(1)}%`}
                                outerRadius={100}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                <Cell fill="#3b82f6" />
                                <Cell fill="#ec4899" />
                              </Pie>
                              <Tooltip 
                                formatter={(value: any, name: string, props: any) => [
                                  `${value} pessoas (${props.payload.percentage.toFixed(1)}%)`,
                                  name
                                ]}
                              />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                          
                          {/* Stats Cards */}
                          <div className="grid grid-cols-3 gap-4 mt-4">
                            <div className="text-center">
                              <p className="text-sm text-gray-600 dark:text-gray-400">Masculino</p>
                              <p className="text-2xl font-bold text-blue-600">{genderData.male}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm text-gray-600 dark:text-gray-400">Feminino</p>
                              <p className="text-2xl font-bold text-pink-600">{genderData.female}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm text-gray-600 dark:text-gray-400">Total</p>
                              <p className="text-2xl font-bold text-gray-900 dark:text-white">{genderData.total}</p>
                          </div>
                        </div>
                      </>
                    ) : (
                        <>
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={[
                                  { name: 'Masculino', value: 1, percentage: 0 },
                                  { name: 'Feminino', value: 1, percentage: 0 },
                                ]}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={(entry: any) => `${entry.name}: 0%`}
                                outerRadius={100}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                <Cell fill="#3b82f6" />
                                <Cell fill="#ec4899" />
                              </Pie>
                              <Tooltip content={() => null} />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="text-center mt-4 text-gray-500 dark:text-gray-400">
                            Aguardando detecções de pessoas...
                          </div>
                        </>
                      )}
                    </div>

                    {/* 2. Gráfico de Idade - BarChart */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                        Distribuição por Faixa Etária
                      </h3>
                      
                      {ageData && ageData.total > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart
                            data={[
                              { faixa: 'Crianças (0-9)', quantidade: ageData.age_0_2 + ageData.age_3_9 },
                              { faixa: 'Adolescentes (10-20)', quantidade: ageData.age_10_20 },
                              { faixa: 'Adultos Jovens (21-37)', quantidade: ageData.age_25_32 },
                              { faixa: 'Adultos (38-53)', quantidade: ageData.age_38_43 + ageData.age_48_53 },
                              { faixa: 'Idosos (60+)', quantidade: ageData.age_60_100 },
                            ]}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="faixa" />
                            <YAxis />
                            <Tooltip 
                              formatter={(value: any) => [`${value} pessoas`, 'Quantidade']}
                            />
                            <Legend />
                            <Bar dataKey="quantidade" fill="#8b5cf6" name="Pessoas" />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                              data={[
                                { faixa: 'Crianças (0-9)', quantidade: 0 },
                                { faixa: 'Adolescentes (10-20)', quantidade: 0 },
                                { faixa: 'Adultos Jovens (21-37)', quantidade: 0 },
                                { faixa: 'Adultos (38-53)', quantidade: 0 },
                                { faixa: 'Idosos (60+)', quantidade: 0 },
                              ]}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="faixa" />
                              <YAxis />
                              <Tooltip content={() => null} />
                              <Legend />
                              <Bar dataKey="quantidade" fill="#e5e7eb" name="Pessoas" />
                            </BarChart>
                          </ResponsiveContainer>
                          <div className="text-center mt-4 text-gray-500 dark:text-gray-400">
                            Aguardando detecções de pessoas...
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* 3. Gráfico de Fluxo de Pessoas por Hora - LineChart */}
                {!loadingDemographics && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                      Fluxo de Pessoas por Hora
                    </h3>
                    
                    {hourlyPeopleData.length > 0 ? (
                      <>
                        <ResponsiveContainer width="100%" height={350}>
                          <LineChart data={hourlyPeopleData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="hour" 
                              label={{ value: 'Hora do dia', position: 'insideBottom', offset: -5 }}
                              tickFormatter={(value) => `${value}:00`}
                            />
                            <YAxis 
                              label={{ value: 'Quantidade de Pessoas', angle: -90, position: 'insideLeft' }}
                            />
                            <Tooltip 
                              formatter={(value: any) => [value, 'Pessoas']}
                              labelFormatter={(label) => `${label}:00`}
                            />
                            <Legend />
                            <Line 
                              type="monotone" 
                              dataKey="people_count" 
                              stroke="#8b5cf6" 
                              strokeWidth={2}
                              name="Pessoas"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                        
                        {/* Estatísticas resumidas */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                          <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                            <p className="text-sm text-purple-600 dark:text-purple-400 mb-1">Total de Pessoas</p>
                            <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                              {hourlyPeopleData.reduce((sum, h) => sum + h.people_count, 0).toLocaleString('pt-BR')}
                            </p>
                          </div>
                          <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg">
                            <p className="text-sm text-indigo-600 dark:text-indigo-400 mb-1">Média por Hora</p>
                            <p className="text-2xl font-bold text-indigo-900 dark:text-indigo-100">
                              {(hourlyPeopleData.reduce((sum, h) => sum + h.people_count, 0) / hourlyPeopleData.length).toFixed(1)}
                            </p>
                            <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">pessoas/hora</p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <ResponsiveContainer width="100%" height={350}>
                          <LineChart data={Array.from({ length: 24 }, (_, i) => ({ hour: i, people_count: 0, vehicles_count: 0, avg_people_per_vehicle: 0 }))}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="hour" 
                              label={{ value: 'Hora do dia', position: 'insideBottom', offset: -5 }}
                            />
                            <YAxis 
                              label={{ value: 'Quantidade', angle: -90, position: 'insideLeft' }}
                            />
                            <Tooltip content={() => null} />
                            <Legend />
                            <Line 
                              type="monotone" 
                              dataKey="people_count" 
                              stroke="#e5e7eb" 
                              strokeWidth={2}
                              name="Pessoas"
                            />
                            <Line 
                              type="monotone" 
                              dataKey="vehicles_count" 
                              stroke="#e5e7eb" 
                              strokeWidth={2}
                              name="Veículos"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                        <div className="text-center mt-4 text-gray-500 dark:text-gray-400">
                          Aguardando detecções de pessoas...
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* 4. Gráfico Semanal - Últimos 7 Dias */}
                {!loadingDemographics && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                      Fluxo dos Últimos 7 Dias
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Comparação de veículos e pessoas por dia da semana
                    </p>
                    
                    {weeklyFlowData.length > 0 ? (
                      <>
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart data={weeklyFlowData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="day_label"
                              tick={(props: any) => {
                                const { x, y, payload } = props;
                                if (!payload?.value) return <g />;
                                
                                // Verificar se é o dia atual
                                const today = new Date();
                                const todayStr = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}`;
                                const isToday = payload.value.includes(todayStr);
                                
                                return (
                                  <g transform={`translate(${x},${y})`}>
                                    <text
                                      x={0}
                                      y={0}
                                      dy={16}
                                      textAnchor="end"
                                      fill={isToday ? '#10b981' : '#6b7280'}
                                      fontSize={12}
                                      fontWeight={isToday ? 'bold' : 'normal'}
                                      transform="rotate(-45)"
                                    >
                                      {payload.value}
                                    </text>
                                  </g>
                                );
                              }}
                              height={100}
                            />
                            <YAxis 
                              label={{ value: 'Quantidade', angle: -90, position: 'insideLeft' }}
                            />
                            <Tooltip 
                              labelFormatter={(label, payload) => {
                                if (payload && payload.length > 0) {
                                  const item = payload[0].payload;
                                  const [year, month, day] = item.date.split('-');
                                  const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                                  const obs = observationsByDate[(item.date || '').slice(0, 10)];
                                  const obsTxt = obs && obs.length
                                    ? ` • 📅 ${obs.map((o) => o.title).join(', ')}`
                                    : '';
                                  return `${item.day_of_week} (${localDate.toLocaleDateString('pt-BR')})${obsTxt}`;
                                }
                                return label;
                              }}
                            />
                            <Legend />
                            <Bar dataKey="vehicles_count" fill="#10b981" name="Veículos">
                              {weeklyFlowData.map((entry, index) => {
                                const obs = observationsByDate[(entry.date || '').slice(0, 10)];
                                return (
                                  <Cell
                                    key={`veh-cell-${index}`}
                                    fill={obs && obs.length ? obs[0].color : '#10b981'}
                                  />
                                );
                              })}
                            </Bar>
                            <Bar dataKey="people_count" fill="#8b5cf6" name="Pessoas" />
                          </BarChart>
                        </ResponsiveContainer>
                        
                        {/* Estatísticas resumidas */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                            <p className="text-sm text-green-600 dark:text-green-400 mb-1">Total de Veículos (7 dias)</p>
                            <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                              {weeklyFlowData.reduce((sum, d) => sum + d.vehicles_count, 0).toLocaleString('pt-BR')}
                            </p>
                            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                              Média: {Math.round(weeklyFlowData.reduce((sum, d) => sum + d.vehicles_count, 0) / 7).toLocaleString('pt-BR')}/dia
                            </p>
                          </div>
                          <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                            <p className="text-sm text-purple-600 dark:text-purple-400 mb-1">Total de Pessoas (7 dias)</p>
                            <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                              {weeklyFlowData.reduce((sum, d) => sum + d.people_count, 0).toLocaleString('pt-BR')}
                            </p>
                            <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                              Média: {Math.round(weeklyFlowData.reduce((sum, d) => sum + d.people_count, 0) / 7).toLocaleString('pt-BR')}/dia
                            </p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart data={[
                            { day_of_week: 'Domingo', vehicles_count: 0, people_count: 0 },
                            { day_of_week: 'Segunda-feira', vehicles_count: 0, people_count: 0 },
                            { day_of_week: 'Terça-feira', vehicles_count: 0, people_count: 0 },
                            { day_of_week: 'Quarta-feira', vehicles_count: 0, people_count: 0 },
                            { day_of_week: 'Quinta-feira', vehicles_count: 0, people_count: 0 },
                            { day_of_week: 'Sexta-feira', vehicles_count: 0, people_count: 0 },
                            { day_of_week: 'Sábado', vehicles_count: 0, people_count: 0 },
                          ]}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="day_of_week" 
                              angle={-45}
                              textAnchor="end"
                              height={100}
                            />
                            <YAxis />
                            <Tooltip content={() => null} />
                            <Legend />
                            <Bar dataKey="vehicles_count" fill="#e5e7eb" name="Veículos" />
                            <Bar dataKey="people_count" fill="#e5e7eb" name="Pessoas" />
                          </BarChart>
                        </ResponsiveContainer>
                        <div className="text-center mt-4 text-gray-500 dark:text-gray-400">
                          Aguardando detecções dos últimos 7 dias...
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>
      <ToastContainer />
    </div>
  );
}
